import React, { useEffect, useState } from "react";
import { CTW_API_ENDPOINT } from "../utils";
import coreAPIService from "../apiServices/coreAPIService";
import { useParams } from "react-router-dom";
import Block from "../components/block";
import Heading from "../common/Heading";
import { sanitizeHtml } from "../utils/sanitize";
import { handleApiError } from "../utils/errorHandler";
import "./Ad.css"
import { Button } from "antd";

interface AdPreviewInterface {
  fbFeedUrl: string | null;
  gramStandardUrl: string | null;
  gramStoryUrl: string | null;
}
interface PreviewResponse {
  meta: any
  data: {
    adId: string;
    adFormat: string;
    html: string;
  };
}

const PreviewCard = ({
  title,
  subtitle,
  html,
  aspect,
  dark = false,
}: {
  title: string;
  subtitle: string;
  html: string;
  aspect: "feed" | "story";
  dark?: boolean;
}) => {
  const sanitizedHtml = sanitizeHtml(html);

  return (
    <div
      className={`rounded-xl border shadow-sm ${dark ? "bg-black text-white" : "bg-white"
        }`}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b text-sm">
        <p className="font-semibold">{title}</p>
        <p className="opacity-70">{subtitle}</p>
      </div>

      {/* Preview */}
      <div className="flex justify-center p-4 bg-gray-50">
        <div
          className={`relative overflow-hidden rounded-xl bg-neutral-100 shadow-inner`}
        >
          <div
            className="preview-frame border border-black/20 border-1-black rounded-xl"
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          />
        </div>
      </div>
    </div>
  );
};



const AdPreview = ({ setOpen, adId }: { setOpen: (open: boolean) => void, adId: string }) => {
  const [previewUrls, setPreviewUrls] = useState<AdPreviewInterface>({
    fbFeedUrl: null,
    gramStandardUrl: null,
    gramStoryUrl: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    const fetchPreviews = async () => {
      setLoading(true);
      setError(null);

      try {
        const responses = await
          coreAPIService.get<PreviewResponse>(
            CTW_API_ENDPOINT, `v2/meta-integration/ads/${adId}/preview?ad_format=FACEBOOK_PROFILE_FEED_MOBILE`
          )
        // coreAPIService.get<PreviewResponse>(
        //   CTW_API_ENDPOINT, `v2/meta-integration/ads/${id}/preview?ad_format=INSTAGRAM_STANDARD`
        // ),
        // coreAPIService.get<PreviewResponse>(
        //   CTW_API_ENDPOINT, `v2/meta-integration/ads/${id}/preview?ad_format=INSTAGRAM_STORY`
        // ),
        // ])

        const urls: AdPreviewInterface = {
          fbFeedUrl: null,
          gramStandardUrl: null,
          gramStoryUrl: null,
        };

        if (responses.data && responses.data?.html) {
          urls.fbFeedUrl = responses.data.html;
        }

        // if (responses[1].status === "fulfilled" && responses[1].value && responses[1].value?.data?.html) {
        //   urls.gramStandardUrl = responses[1].value.data.html;
        // }

        // if (responses[2].status === "fulfilled" && responses[2].value && responses[2].value?.data?.html) {
        //   urls.gramStoryUrl = responses[2].value.data.html;
        // }

        setPreviewUrls(urls);
      } catch (err) {
        handleApiError(err, 'ad-preview-fetch', 'Failed to fetch ad previews');
        setError("Failed to fetch ad previews.");
      } finally {
        setLoading(false);
      }
    };

    fetchPreviews();
  }, [id]);

  useEffect(() => {
    const adjustIframeSize = () => {
      // Target all iframes, not just the first one
      const iframes = document.querySelectorAll(".preview-frame iframe");
      iframes.forEach((iframe) => {
        if (iframe instanceof HTMLIFrameElement) {
          try {
            // Set responsive dimensions
            iframe.style.height = "600px";
            iframe.style.width = "100%";
            iframe.style.maxWidth = "400px";
            iframe.style.border = "none";
            iframe.style.borderRadius = "8px";

            // Remove the entire PreviewCard if "Your ad won't run" error is present
            const removeWarning = () => {
              try {
                const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                if (iframeDoc) {
                  let hasError = !!iframeDoc.querySelector('[data-preview-error="true"]');

                  if (!hasError) {
                    const walker = iframeDoc.createTreeWalker(iframeDoc.body, NodeFilter.SHOW_TEXT, null);
                    let node;
                    while (node = walker.nextNode()) {
                      if (node.textContent?.includes("Your ad won't run")) {
                        hasError = true;
                        break;
                      }
                    }
                  }

                  if (hasError) {
                    // Find the PreviewCard container in the parent document and remove it
                    const previewCard = iframe.closest('.rounded-xl.border.shadow-sm');
                    if (previewCard) {
                      previewCard.remove();
                    }
                  }
                }
              } catch (e) {
                // Cross-origin access might fail
              }
            };

            // Run immediately and also after a short delay to catch dynamic content
            removeWarning();
            setTimeout(removeWarning, 1000);
            setTimeout(removeWarning, 3000);

          } catch (err) {
            // Iframe size adjustment error handled silently
          }
        }
      });
    };

    if (previewUrls.fbFeedUrl || previewUrls.gramStandardUrl || previewUrls.gramStoryUrl) {
      // Delay to ensure DOM is ready
      setTimeout(adjustIframeSize, 100);
    }
  }, [previewUrls]);


  return (
    <Block>
      <div className="ad-preview-container">
        <Heading title={"Meta Ad Preview"} />
        <Button className="ml-auto bg-[#1F344C] text-white" onClick={() => setOpen(false)}>Back</Button>

        {error && <p className="text-red-500">{error}</p>}
        {loading ? (
          <p>Loading previews...</p>
        ) : (
          <section className="px-10 py-6">
            <div className="grid grid-cols-1 md:grid-cols-1 xl:grid-cols-1 gap-8 m-auto">

              {/* Facebook Feed */}
              {previewUrls.fbFeedUrl && (
                <PreviewCard
                  title="Facebook Feed"
                  subtitle="Profile Feed Placement"
                  aspect="feed"
                  html={previewUrls.fbFeedUrl}
                />
              )}

              {/* Instagram Feed */}
              {/* {previewUrls.gramStandardUrl && (
                <PreviewCard
                  title="Instagram Feed"
                  subtitle="Standard Placement"
                  aspect="feed"
                  html={previewUrls.gramStandardUrl}
                  dark
                />
              )} */}

              {/* Instagram Story */}
              {/* {previewUrls.gramStoryUrl && (
                <PreviewCard
                  title="Instagram Story"
                  subtitle="Full Screen Story"
                  aspect="story"
                  html={previewUrls.gramStoryUrl}
                  dark
                />
              )} */}
            </div>
          </section>

        )}
      </div>
    </Block>
  );
};

export default AdPreview;
